import { gql } from './urqlHooks';
import { useQuery, useMutation } from './urqlHooks';

// Queries
const GET_RULES_BY_COMPANY = gql`
  query GetRulesByCompany($companyId: String!) {
    getRulesByCompany(companyId: $companyId) {
      status
      message
      rules {
        id
        companyId
        ruleType
        description
        transactionType
        type
        typeLabel
        isReceived
        isDocumentPaymentMethod
        agendaId
        contactRut
        contactName
        documentSubType
        accountKey
        accountName
        counterpartAccountKey
        counterpartAccountName
        priority
        isEnabled
        isDefault
        isHidden
        createdBy {
          userId
          userEmail
          userName
        }
        createdAt
        updatedAt
        editionHistory {
          date
          userId
          userEmail
          action
          changes
        }
        exceptions {
          filters {
            kind
            agendaId
            value
          }
          accountKey
          accountName
        }
        documentExceptions {
          filters {
            kind
            value
            amount
            operator
            joinOperator
          }
          accountKey
          accountName
        }
        costCenterAllocations {
          costCenterId
          percent
        }
      }
    }
  }
`;

const FIND_MATCHING_RULE = gql`
  query FindMatchingRule($companyId: String!, $transactionType: TransactionTypeForRule!, $type: String!) {
    findMatchingRule(companyId: $companyId, transactionType: $transactionType, type: $type) {
      id
      accountKey
      accountName
      description
      priority
    }
  }
`;

// Mutations
const CREATE_RULE = gql`
  mutation CreateRule($companyId: String!, $input: CreateRuleInput!) {
    createRule(companyId: $companyId, input: $input) {
      status
      message
      rule {
        id
        ruleType
        description
        transactionType
        type
        typeLabel
        isReceived
        isDocumentPaymentMethod
        agendaId
        contactRut
        contactName
        accountKey
        accountName
        counterpartAccountKey
        counterpartAccountName
        priority
        isEnabled
        isDefault
        isGlobalDocument
        documentSubType
        exceptions {
          filters {
            kind
            agendaId
            value
          }
          accountKey
          accountName
        }
        documentExceptions {
          filters {
            kind
            value
            amount
            operator
            joinOperator
          }
          accountKey
          accountName
        }
        costCenterAllocations {
          costCenterId
          percent
        }
      }
    }
  }
`;

const UPDATE_RULE = gql`
  mutation UpdateRule($input: UpdateRuleInput!, $companyId: String!) {
    updateRule(input: $input, companyId: $companyId) {
      status
      message
      rule {
        id
        ruleType
        description
        transactionType
        type
        typeLabel
        isReceived
        isDocumentPaymentMethod
        agendaId
        contactRut
        contactName
        accountKey
        accountName
        counterpartAccountKey
        counterpartAccountName
        priority
        isEnabled
        isDefault
        isGlobalDocument
        documentSubType
        updatedAt
        exceptions {
          filters {
            kind
            agendaId
            value
          }
          accountKey
          accountName
        }
        documentExceptions {
          filters {
            kind
            value
            amount
            operator
            joinOperator
          }
          accountKey
          accountName
        }
        costCenterAllocations {
          costCenterId
          percent
        }
      }
    }
  }
`;

const DELETE_RULE = gql`
  mutation DeleteRule($ruleId: String!, $companyId: String!) {
    deleteRule(ruleId: $ruleId, companyId: $companyId) {
      status
      message
      rule {
        id
        isHidden
      }
    }
  }
`;

const TOGGLE_RULE_STATUS = gql`
  mutation ToggleRuleStatus($input: ToggleRuleStatusInput!, $companyId: String!) {
    toggleRuleStatus(input: $input, companyId: $companyId) {
      status
      message
      rule {
        id
        isEnabled
        updatedAt
      }
    }
  }
`;

const GET_GLOBAL_DOCUMENT_RULE = gql`
  query GetGlobalDocumentRule($companyId: String!, $isReceived: Boolean!, $documentSubType: DocumentSubType) {
    getGlobalDocumentRule(companyId: $companyId, isReceived: $isReceived, documentSubType: $documentSubType) {
      id
      companyId
      ruleType
      isReceived
      isGlobalDocument
      documentSubType
      accountKey
      accountName
      description
      isEnabled
      isHidden
      updatedAt
      documentExceptions {
        filters {
          kind
          value
          amount
          operator
          joinOperator
        }
        accountKey
        accountName
      }
      costCenterAllocations {
        costCenterId
        percent
      }
    }
  }
`;

const GET_DOCUMENT_RULES_BY_AGENDA_IDS = gql`
  query GetDocumentRulesByAgendaIds($companyId: String!, $agendaIds: [String!]!, $isReceived: Boolean!, $documentSubType: DocumentSubType) {
    getDocumentRulesByAgendaIds(companyId: $companyId, agendaIds: $agendaIds, isReceived: $isReceived, documentSubType: $documentSubType) {
      id
      companyId
      ruleType
      description
      isReceived
      agendaId
      contactRut
      contactName
      documentSubType
      accountKey
      accountName
      priority
      isEnabled
      isDefault
      isHidden
      updatedAt
      documentExceptions {
        filters {
          kind
          value
          amount
          operator
          joinOperator
        }
        accountKey
        accountName
      }
      costCenterAllocations {
        costCenterId
        percent
      }
    }
  }
`;

const CREATE_DEFAULT_RULES = gql`
  mutation CreateDefaultRules($companyId: String!) {
    createDefaultRules(companyId: $companyId) {
      status
      message
      rules {
        id
        description
        transactionType
        type
        typeLabel
        accountKey
        accountName
        priority
        isEnabled
        isDefault
      }
    }
  }
`;

// Hooks
export const GetRulesByCompany = (variables) => {
  const [result, executeQuery] = useQuery({
    query: GET_RULES_BY_COMPANY,
    variables,
    pause: !variables?.companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery };
};

export const FindMatchingRule = (variables) => {
  return useQuery({
    query: FIND_MATCHING_RULE,
    variables,
    pause: !variables?.companyId || !variables?.transactionType || !variables?.type,
  });
};

export const CreateRule = () => {
  const [result, executeMutation] = useMutation(CREATE_RULE);
  return { result, executeMutation };
};

export const UpdateRule = () => {
  const [result, executeMutation] = useMutation(UPDATE_RULE);
  return { result, executeMutation };
};

export const DeleteRule = () => {
  const [result, executeMutation] = useMutation(DELETE_RULE);
  return { result, executeMutation };
};

export const ToggleRuleStatus = () => {
  const [result, executeMutation] = useMutation(TOGGLE_RULE_STATUS);
  return { result, executeMutation };
};

export const GetDocumentRulesByAgendaIds = (variables) => {
  const [result, executeQuery] = useQuery({
    query: GET_DOCUMENT_RULES_BY_AGENDA_IDS,
    variables,
    pause: !variables?.companyId || !variables?.agendaIds?.length || variables?.isReceived === undefined,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery };
};

export const CreateDefaultRules = () => {
  const [result, executeMutation] = useMutation(CREATE_DEFAULT_RULES);
  return { result, executeMutation };
};

export const GetGlobalDocumentRule = (variables) => {
  const [result, executeQuery] = useQuery({
    query: GET_GLOBAL_DOCUMENT_RULE,
    variables,
    pause: !variables?.companyId || variables?.isReceived === undefined,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, executeQuery };
};

